import { formatDurationHuman } from '@/utils'

import { CueRunState } from '~shared/utils'
import type { Timestamp } from '~shared/utils'

type DurationTopDisplayProps = {
  cueState?: Timestamp['state'] | null
  actualDuration?: number
  originalDuration?: number
  plannedDuration?: number
}

/**
 * DurationTopDisplay Component
 *
 * Displays the original/planned duration above the main duration display when
 * relevant. Smart component that determines its own visibility based on cue
 * state and duration differences.
 */
export function DurationTopDisplay({
  cueState,
  actualDuration,
  originalDuration,
  plannedDuration,
}: DurationTopDisplayProps) {
  // Determine whether the component should display anything
  const shouldDisplay = determineVisibility(
    cueState,
    actualDuration,
    originalDuration,
    plannedDuration,
  )

  // If nothing to display, return null
  if (!shouldDisplay) return null

  // Calculate the appropriate text to display
  const displayText = getDisplayText(
    cueState,
    actualDuration,
    originalDuration,
    plannedDuration,
  )

  // Determine if text should be strikethrough
  const shouldStrikethrough = determineStrikethrough(
    cueState,
    actualDuration,
    originalDuration,
  )

  return (
    <p className="text-xs text-white/60 tabular-nums truncate">
      <span className={shouldStrikethrough ? 'line-through' : ''}>
        {displayText}
      </span>
    </p>
  )
}

/**
 * Determines whether the top display should be shown
 */
function determineVisibility(
  cueState: Timestamp['state'] | null | undefined,
  actualDuration: number | undefined,
  originalDuration: number | undefined,
  plannedDuration: number | undefined,
) {
  // For active, show the planned duration
  if (cueState === CueRunState.CUE_ACTIVE) {
    return true
  }

  // For past cues, show the planned duration if different from actual
  if (cueState === CueRunState.CUE_PAST) {
    return plannedDuration !== actualDuration
  }

  // For future cues, show original duration if it has changed
  if (
    cueState === CueRunState.CUE_NEXT ||
    cueState === CueRunState.CUE_FUTURE
  ) {
    return originalDuration !== actualDuration
  }

  return false
}

/**
 * Gets the text to display in the top component
 */
function getDisplayText(
  cueState: Timestamp['state'] | null | undefined,
  _actualDuration: number | undefined,
  originalDuration: number | undefined,
  plannedDuration: number | undefined,
) {
  // For active/past cues, show the planned duration
  if (
    cueState === CueRunState.CUE_ACTIVE ||
    cueState === CueRunState.CUE_PAST
  ) {
    return formatDurationHuman(plannedDuration ?? 0)
  }

  // For future cues, show the original duration
  return formatDurationHuman(originalDuration ?? 0)
}

/**
 * Determines if the text should have strikethrough styling
 */
function determineStrikethrough(
  cueState: Timestamp['state'] | null | undefined,
  actualDuration: number | undefined,
  originalDuration: number | undefined,
) {
  // Past cues always get strikethrough
  if (cueState === CueRunState.CUE_PAST) {
    return true
  }

  // Future cues with changed duration get strikethrough
  if (
    cueState === CueRunState.CUE_NEXT ||
    cueState === CueRunState.CUE_FUTURE
  ) {
    return originalDuration !== actualDuration
  }

  return false
}
